import {useEffect, useRef} from "react";
import {renderAsync} from "docx-preview";
import FtpService from "../services/ftp.service";
import * as Sentry from "@sentry/react";

/**
 * @author KMU
 * Custom Preview types as explicit types or element functions
 */

export type PreviewItem = {
    type: "image" | "pdf" | "docx";
    url?: string;
    buffer?: ArrayBuffer;
    name?: string
}

export type DocxFile = {
    buffer: ArrayBuffer;
    name: string;
};

/**
 * UI element for docx attachment previews
 * @param docx docx content
 * @param index index of the docx file for unique ids
 * @constructor
 */
export function DocxSlide({docx, index}: { docx: DocxFile | null, index: number }) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            renderAsync(docx?.buffer, ref.current)
                .then(() => {
                    window.dispatchEvent(new Event("resize"));
                })
                .catch(console.error);
        }
    }, [docx]);

    return (
        <div
            ref={ref}
            id={`docx-preview-${index}`}
            className="preview-item"
        />
    )
}

/**
 * Load the preview attachments for the given attachments
 * @param attachments
 */
export async function loadPreviewItems(attachments: string[]) {
    const items: Array<PreviewItem> = [];

    for (const filename of attachments) {
        const fileType = filename.split('.').pop()?.toLowerCase();
        try {
            const url = await FtpService.getPreviewUrl(filename);
            if (fileType === 'docx' || fileType === 'doc') {
                const buffer = await fetch(url).then(res => res.arrayBuffer());
                items.push({type: "docx", buffer, name: filename});
            } else if (["png", "jpg", "jpeg"].includes(fileType || "")) {
                items.push({type: "image", url});
            } else if (fileType === "pdf") {
                items.push({type: "pdf", url});
            }
        } catch (error) {
            Sentry.captureException(error, {
                extra: {
                    request: {
                        filename: JSON.parse(JSON.stringify(filename)),
                    },
                },
            });
        }
    }

    return items;
}